import { useCallback, useMemo } from "react";
import { useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { useRemoteFetchNoPlaceZoneBookingScheduleMutation } from "../../../../hooks/Remote/Schedule/useRemoteFetchNoPlaceZoneBookingScheduleMutation";
import { useRemoteFetchPlaceScheduleMutation } from "../../../../hooks/Remote/Schedule/useRemoteFetchPlaceScheduleMutation";
import { useRemoteFetchVehicleScheduleMutation } from "../../../../hooks/Remote/Schedule/useRemoteFetchVehicleScheduleMutation";
import { useRemoteFetchResourceScheduleMutation } from "../../../../hooks/Remote/Schedule/useRemoteFetchResourceScheduleMutation";
import { useRemoteResourceScheduleByBookingObjectMutation } from "../../../../hooks/Remote/Schedule/useRemoteResourceScheduleByBookingObjectMutation";
import {
  fetchSchedulesForExcelExport,
  type ScheduleExcelExportFetchConfig,
  type ScheduleExcelExportFetchDeps
} from "../functions/schedule-excel-export-fetch.functions";

export function useScheduleExcelExportFetch(fetchConfig: ScheduleExcelExportFetchConfig) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { mutateAsync: fetchPlaceSchedule } = useRemoteFetchPlaceScheduleMutation();
  const { mutateAsync: fetchNoPlaceZoneSchedule } =
    useRemoteFetchNoPlaceZoneBookingScheduleMutation();
  const { mutateAsync: fetchVehicleSchedule } = useRemoteFetchVehicleScheduleMutation();
  const { mutateAsync: fetchResourceSchedule } = useRemoteFetchResourceScheduleMutation();
  const { mutateAsync: fetchResourceScheduleByObject } =
    useRemoteResourceScheduleByBookingObjectMutation();

  const deps = useMemo(
    (): ScheduleExcelExportFetchDeps => ({
      colleague: {
        fetchPlaceSchedule,
        fetchNoPlaceZoneSchedule,
        fetchVehicleSchedule,
        fetchResourceSchedule
      },
      resource: {
        queryClient,
        fetchResourceScheduleByObject
      }
    }),
    [
      queryClient,
      fetchPlaceSchedule,
      fetchNoPlaceZoneSchedule,
      fetchVehicleSchedule,
      fetchResourceSchedule,
      fetchResourceScheduleByObject
    ]
  );

  const fetchSchedulesForPeriod = useCallback(
    async (period: { startDate: string; endDate: string }): Promise<BookingScheduleInterface[]> => {
      try {
        return await fetchSchedulesForExcelExport(fetchConfig, period, deps);
      } catch {
        enqueueSnackbar(t("_scheduleExcelExport_fetchError"), { variant: "error" });
        return [];
      }
    },
    [deps, enqueueSnackbar, fetchConfig, t]
  );

  return { fetchSchedulesForPeriod };
}
